import React, { useState } from 'react'
import * as R from 'ramda'
import _ from 'lodash'

import Table from 'shared/components/Table'
import { Button, Input, Form, Dropdown, Label, Popup } from 'semantic-ui-react'
import { TooltipLabel } from 'components/TooltipLabel'
import useModal from 'hooks/useModal'
import CyclrLookupModalContainer from './CyclrLookupModal/container'
import { isVariableField, mapVariablesToDataSource } from './Variables/utils'
import { Tooltip } from 'antd'
import { VariableFieldValue } from './Variables/VariableFieldValue'
import { excludeCustomFields } from './utils'

type CyclrDestinationField = {
  Field: CyclrSourceField
  MappingType: 'Ignore' | 'PreviousStep' | 'StaticValue' | 'ValueList'
  SourceFieldId: number
  SourceStepId: string
  TriggerValue: string
  TriggerValueDisplayName: string
  Value: string
  IsLaunchVisible: boolean
}

type Destination = {
  step_id: string
  fields: CyclrDestinationField[]
}

type CyclrSourceField = {
  Id: number
  Name: string
  Description: string
  IsOptional: boolean
  DataType: 'Text'
  TriggerName: string
  Values: string[]
  DisplayOrder: number
  Triggers: string[]
}

type Source = {
  step_id: string
  fields: CyclrSourceField[]
}

type Variable = {
  DisplayName: string
  SystemName: string
  Value: string
  MappingType: string
  fields: CyclrSourceField[]
}

type AdditionalSettings = {
  source: Source
  destination: Destination
  variables: Variable[]
}

interface CyclrCustomFieldFormProps {
  connectorProperties: string[]
  additionalSettings: AdditionalSettings
  onChange: Function
  isSaving: boolean
  isDisabled: boolean
  isTemplateActive: boolean
  handleCallLookupField: Function
  lookupResponseData: any
  lookupLoading: boolean
  settingType: 'custom_field' | 'variable'
}
export default function CyclrCustomFieldForm({
  connectorProperties,
  additionalSettings,
  onChange,
  isSaving,
  isDisabled,
  isTemplateActive,
  handleCallLookupField,
  lookupResponseData,
  lookupLoading,
  settingType,
}: Readonly<CyclrCustomFieldFormProps>) {
  const [dataSource, setDataSource] = useState(
    getInitialDataSource(additionalSettings, connectorProperties)
  )
  const destination = additionalSettings?.destination
  const source = additionalSettings?.source
  const { Modal, open, close, isOpen } = useModal()
  const [lookupModalDataSourceIndex, setLookupModalDataSourceIndex] = useState(null)
  const sourceOptions = getSourceOptions(source)
  const sourceStepId = R.prop('step_id', source)
  const fieldTitle = !R.isNil(destination) ? `${destination['connector_name']} field` : 'Field'

  const columns = [
    {
      title: fieldTitle,
      dataIndex: 'Field',
      width: '30%',
      ellipsis: true,
      render: (item) => {
        const dataTypeLabels = { Integer: 'Number', survey_selector: 'Survey' }
        const dataType = dataTypeLabels[item.DataType] || item.DataType
        return (
          <div>
            <Popup
              basic
              hoverable
              trigger={<span className="mr-1">{item.Name} </span>}
              content={item.Description}
              className="wide"
              disabled={!item.Description}
            />
            {item?.IsOptional == false && (
              <Tooltip title="This field is required">
                <i className="far fa-exclamation-triangle icon text-orange" />
              </Tooltip>
            )}
            {item.DataType !== 'Undefined' && <Label basic>{_.capitalize(dataType)}</Label>}
          </div>
        )
      },
    },
    {
      title: 'Action',
      dataIndex: 'MappingType',
      width: '30%',
      render: (mappingType, row, index) => {
        const hasTrigger = !R.isEmpty(row.Field.Triggers)
        const options = isVariableField(row)
          ? [
              {
                key: 'StaticValue',
                value: 'StaticValue',
                text: (
                  <span>
                    <i className="text-grey fal fa-pencil" />
                    <span className="ml-2">Type a value</span>
                  </span>
                ),
              },
            ]
          : mappingTypeOptions(
              R.prop('connector_icon', source),
              R.prop('connector_name', source),
              row.Field.Values,
              hasTrigger
            )

        return (
          <Dropdown
            value={mappingType}
            fluid
            selection
            options={options}
            onChange={(_, { value }) => handleMappingTypeChange(value, index, row.Field.Values)}
            disabled={isDisabled || isSaving}
          />
        )
      },
    },
    {
      title: 'Field value',
      width: '40%',
      render: (_, row, index) => {
        if (isVariableField(row)) {
          return (
            <VariableFieldValue
              row={row}
              onChange={(value) => handleStaticValueChange(value, index)}
              isDisabled={isDisabled || isSaving}
            />
          )
        }

        let triggerValueDisplayName = row.TriggerValueDisplayName

        if (!triggerValueDisplayName) {
          try {
            const triggerValues = JSON.parse(row.Value)
            triggerValueDisplayName = triggerValues['name']
          } catch {
            triggerValueDisplayName = ''
          }
        }

        return (
          <SourceColumn
            mappingType={row.MappingType}
            textValue={row.Value}
            triggerValueDisplayName={triggerValueDisplayName}
            sourceFieldId={row.SourceFieldId}
            valueList={prepareValueList(row.Field.Values)}
            sourceOptions={sourceOptions}
            dataSourceIndex={index}
            onStaticValueChange={handleStaticValueChange}
            onSourceFieldChange={handleSourceFieldChange}
            setLookupModalDataSourceIndex={handleClickLookupIcon}
            initialLookupFieldModal={initialLookupFieldModal}
            isDisabled={isDisabled || isSaving}
            destinationFieldId={row.Field.Id}
          />
        )
      },
    },
  ]

  function handleClickLookupIcon(dataSourceIndex) {
    setLookupModalDataSourceIndex(dataSourceIndex)

    if (!isTemplateActive) {
      const rowData = dataSource[dataSourceIndex]
      handleCallLookupField(
        {
          trigger_id: rowData.Field.Triggers[0].TriggerId,
          step_id: destination.step_id,
          entity_id: rowData.Field.Id,
          entity_type: rowData.IsParameter ? 'Parameter' : 'Field',
        },
        true
      )
    }

    open()
  }

  function getInitialDataSource(additionalSettings, connectorProperties) {
    const destination = additionalSettings?.destination
    const source = additionalSettings?.source
    const variables = additionalSettings?.variables?.map(mapVariablesToDataSource) || []

    if (destination) {
      const destinationParameters = destination.parameters
        .filter((parameter) => {
          const isParameterNotIncluded = !connectorProperties.includes(
            parameter.Parameter.TargetName
          )
          const hasMappingType = parameter.MappingType !== null
          return isParameterNotIncluded && hasMappingType
        })
        .map((parameter) => {
          const addedProperties = {
            Field: parameter.Parameter,
            IsParameter: true,
          }
          return { ...parameter, ...addedProperties }
        })
      const destinationParametersAndFields = [...destinationParameters, ...destination.fields]

      /*
       * Exclude fields that come from a different step other than our source step
       * For more information, visit:
       * <a href="https://github.com/simplesat/simplesat/issues/5891"></a>
       */
      const customFields = excludeCustomFields(destinationParametersAndFields, source.step_id)
      return [...variables, ...customFields]
    } else if (variables) {
      return variables
    } else {
      return []
    }
  }

  function getSourceOptions(source) {
    if (!source) {
      return []
    } else {
      return source.fields.map(({ Id, Name, DataType }) => ({
        key: Id,
        value: Id,
        text: Name,
        DataType,
      }))
    }
  }

  function prepareValueList(valueList) {
    return valueList.map((value) => ({
      value: value,
      text: value,
    }))
  }

  function handleMappingTypeChange(value, dataSourceIndex, valueList) {
    setDataSource((dataSource) => {
      const field = {
        ...dataSource[dataSourceIndex],
        MappingType: value,
        isEdited: true,
        Value: null,
        SourceFieldId: null,
        SourceStepId: null,
        ...getDefaultFieldsFromMappingType(value),
      }
      return R.assocPath([dataSourceIndex], field, dataSource)
    })

    function getDefaultFieldsFromMappingType(mappingType) {
      if (mappingType === 'PreviousStep') {
        const firstSourceFieldId = R.path([0, 'value'], sourceOptions)
        const field = {
          Value: null,
          SourceFieldId: firstSourceFieldId,
          SourceStepId: sourceStepId,
        }
        return field
      } else if (value === 'Ignore' || value === 'StaticValue') {
        const emptyValue = {
          Value: null,
          SourceFieldId: null,
          SourceStepId: null,
        }
        return emptyValue
      } else if (value === 'ValueList') {
        const firstValue = {
          Value: valueList[0],
          SoureFieldId: null,
          SourceStepId: null,
        }
        return firstValue
      } else if (value === 'TriggerMethod') {
        const emptyTriggerValue = {
          Value: null,
          SoureFieldId: null,
          SourceStepId: null,
          TriggerValue: null,
          TriggerValueDisplayName: null,
        }
        return emptyTriggerValue
      }
    }

    if (value === 'TriggerMethod') {
      handleClickLookupIcon(dataSourceIndex)
    }
  }

  function handleStaticValueChange(value, dataSourceIndex) {
    setDataSource((dataSource) => {
      const field = {
        ...dataSource[dataSourceIndex],
        Value: value,
        isEdited: true,
      }
      return R.assocPath([dataSourceIndex], field, dataSource)
    })
  }

  function handleTriggerValueChange({ value, text }, dataSourceIndex) {
    setDataSource((dataSource) => {
      const field = {
        ...dataSource[dataSourceIndex],
        TriggerValue: value,
        TriggerValueDisplayName: text,
        isEdited: true,
      }
      return R.assocPath([dataSourceIndex], field, dataSource)
    })
  }

  function handleSourceFieldChange(sourceFieldId, dataSourceIndex) {
    setDataSource((dataSource) => {
      const field = {
        ...dataSource[dataSourceIndex],
        Value: null,
        SourceFieldId: sourceFieldId,
        SourceStepId: sourceStepId,
        isEdited: true,
      }
      return R.assocPath([dataSourceIndex], field, dataSource)
    })
  }

  function handleSubmit() {
    const dataSourceEdited = dataSource.filter((field) => R.prop('isEdited', field) === true)
    onChange({ customFields: dataSourceEdited, destinationStepId: destination?.step_id })
  }

  const initialLookupFieldModal = (dataIndex) => {
    const targetIndex = R.isNil(lookupModalDataSourceIndex) ? dataIndex : lookupModalDataSourceIndex

    if (R.isNil(lookupModalDataSourceIndex)) {
      setLookupModalDataSourceIndex(dataIndex)
      return
    }

    if (!isTemplateActive) {
      const rowData = dataSource[targetIndex]

      handleCallLookupField(
        {
          trigger_id: rowData.Field.Triggers[0].TriggerId,
          step_id: destination.step_id,
          entity_id: rowData.Field.Id,
          entity_type: rowData.IsParameter ? 'Parameter' : 'Field',
        },
        true
      )
    }

    open()
  }

  const hasField = !R.isEmpty(dataSource)

  function filteredColumns(columns) {
    return settingType === 'variable'
      ? columns.filter((column) => column.title !== 'Action')
      : columns
  }

  return (
    <>
      <h4>
        Field mapping
        <TooltipLabel
          descriptionTitle="Field mapping"
          descriptionContent='Map Simplesat response fields to any field in your helpdesk or CRM. For example, you can map ratings to a "latest_nps" contact field in your CRM to make sure records are always up to date.'
          learnMoreLink="https://help.simplesat.io/en/articles/6168765-field-mapping"
        />
      </h4>
      <div className="relative z-10 mb-6">
        <Form onSubmit={handleSubmit}>
          <Table
            variant="segment"
            clickable={false}
            className="mt-4"
            dataSource={dataSource}
            columns={filteredColumns(columns)}
          />
          {hasField && (
            <div className="flex justify-end !mt-5">
              <Button primary type="submit" disabled={isDisabled || isSaving} loading={isSaving}>
                Save
              </Button>
            </div>
          )}
        </Form>

        {isOpen && (
          <CyclrLookupModalContainer
            Modal={Modal}
            close={close}
            lookupModalDataSourceIndex={lookupModalDataSourceIndex}
            rowData={dataSource[lookupModalDataSourceIndex]}
            destinationStepId={destination.step_id}
            lookupResponseData={lookupResponseData}
            lookupLoading={lookupLoading}
            onAddField={handleTriggerValueChange}
            isTemplateActive={isTemplateActive}
            onSelectTrigger={handleCallLookupField}
          />
        )}
      </div>
    </>
  )
}

const mappingTypeOptions = (sourceIcon, sourceConnectorName, valueList, hasTrigger) => {
  let iconSrc
  if (!R.isNil(sourceIcon) && sourceConnectorName !== 'Simplesat') {
    iconSrc = sourceIcon
  } else {
    iconSrc = 'https://cdn.simplesat.io/images/simplesat-dropdown-icon.png'
  }

  function getSelectValueOptions() {
    if (valueList.length) {
      return [
        {
          key: 'ValueList',
          value: 'ValueList',
          text: (
            <span>
              <i className="text-grey far fa-hand-pointer" />
              <span className="ml-2">Select a value</span>
            </span>
          ),
        },
      ]
    } else {
      return []
    }
  }

  const mappingOptions = [
    {
      key: 'Ignore',
      value: 'Ignore',
      text: (
        <span>
          <i className="text-grey far fa-ban" />
          <span className="ml-2">Ignore</span>
        </span>
      ),
    },
    {
      key: 'PreviousStep',
      value: 'PreviousStep',
      text: (
        <span className="flex items-center">
          <img
            src={iconSrc}
            alt="simplesat-dropdown-icon"
            width="24"
            height="24"
            className="absolute left-4"
          />
          <span className="ml-7">Use data from response</span>
        </span>
      ),
    },
    {
      key: 'StaticValue',
      value: 'StaticValue',
      text: (
        <span>
          <i className="text-grey fal fa-pencil" />
          <span className="ml-2">Type a value</span>
        </span>
      ),
    },
    ...getSelectValueOptions(),
  ]

  if (hasTrigger) {
    mappingOptions.push({
      key: 'Lookup',
      value: 'TriggerMethod',
      text: (
        <React.Fragment>
          <i className="text-grey far fa-magnifying-glass" />
          <span className="ml-2">Lookup</span>
        </React.Fragment>
      ),
    })
  }

  return mappingOptions
}

type SourceColumnProps = {
  mappingType: 'Ignore' | 'PreviousStep' | 'StaticValue' | 'ValueList'
  textValue?: string
  sourceFieldId?: number
  dataSourceIndex: number
  valueList: any[]
  sourceOptions: any[]
  onStaticValueChange: Function
  onSourceFieldChange: Function
  isDisabled: boolean
  triggerValueDisplayName?: string
  destinationFieldId?: number
  setLookupModalDataSourceIndex
  initialLookupFieldModal: Function
}
function SourceColumn({
  mappingType,
  textValue,
  sourceFieldId,
  valueList,
  sourceOptions,
  dataSourceIndex,
  onStaticValueChange,
  onSourceFieldChange,
  isDisabled,
  triggerValueDisplayName,
  destinationFieldId,
  initialLookupFieldModal,
}: SourceColumnProps) {
  if (mappingType === 'Ignore') {
    return null
  } else if (mappingType === 'StaticValue') {
    return (
      <Input
        key={destinationFieldId + mappingType}
        type="text"
        fluid
        required
        value={textValue}
        onChange={(_, { value }) => onStaticValueChange(value, dataSourceIndex)}
        disabled={isDisabled}
      />
    )
  } else if (mappingType === 'PreviousStep') {
    return (
      <Dropdown
        value={sourceFieldId}
        fluid
        search
        selection
        options={sourceOptions}
        onChange={(_, { value }) => onSourceFieldChange(value, dataSourceIndex)}
        disabled={isDisabled}
      />
    )
  } else if (mappingType === 'ValueList') {
    return (
      <Dropdown
        value={textValue}
        fluid
        search
        selection
        options={valueList}
        onChange={(_, { value }) => onStaticValueChange(value, dataSourceIndex)}
        disabled={isDisabled}
      />
    )
  } else if (mappingType === 'TriggerMethod') {
    return (
      <Input
        key={destinationFieldId + mappingType + triggerValueDisplayName}
        className="align-center"
        type="text"
        fluid
        required
        value={triggerValueDisplayName}
        disabled
        action={
          <Button
            onClick={(e) => {
              e.preventDefault()
              initialLookupFieldModal(dataSourceIndex)
            }}
            basic
          >
            <i className="far fa-magnifying-glass" />
          </Button>
        }
      />
    )
  }
  return null
}



// WEBPACK FOOTER //
// ./src/components/CyclrCustomFieldForm/index.tsx